import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full border border-slate-200 bg-[#FFFFFF]/60 px-4 py-3.5 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors duration-300 focus:border-[#6D28D9]/60";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in.");
      navigate("/client/login");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="hero-spotlight flex min-h-screen flex-col bg-[#FFFFFF] text-slate-900">
      <header className="border-b border-slate-200 bg-[#FFFFFF]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="PRE-IPO EXPERT" className="h-10 w-10 rounded-full object-contain" />
            <span className="font-display text-lg font-semibold tracking-[0.18em]">PRE-IPO <span className="text-[#6D28D9]">EXPERT</span></span>
          </Link>
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-md">
          <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#6D28D9]/90">Account Security</p>
          <h1 className="font-display mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">Choose a new password</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">Minimum 8 characters. This link works once and expires 30 minutes after it was sent.</p>
          {!token ? (
            <p data-testid="reset-missing-token" className="mt-8 border border-rose-600/30 bg-rose-600/[0.06] p-5 text-sm text-rose-700">
              This reset link is incomplete. <Link to="/client/login" className="underline">Request a new one</Link>.
            </p>
          ) : (
            <form data-testid="reset-password-form" onSubmit={submit} className="mt-8 space-y-4 border border-slate-200 bg-[#F6F4FD]/70 p-8 backdrop-blur">
              <input data-testid="reset-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" className={inputCls} />
              <input data-testid="reset-confirm-input" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className={inputCls} />
              {error && <p data-testid="reset-error" className="text-xs text-rose-600">{error}</p>}
              <button data-testid="reset-submit-button" type="submit" disabled={loading} className="w-full bg-[#6D28D9] px-8 py-4 font-mono2 text-xs font-semibold uppercase tracking-[0.22em] text-[#FFFFFF] transition-colors duration-300 hover:bg-[#5B21B6] disabled:opacity-50">
                {loading ? "Updating…" : "Update password"}
              </button>
            </form>
          )}
          <Link to="/client/login" className="mt-6 flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#6D28D9]">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
          </Link>
        </motion.div>
      </main>
    </div>
  );
}
